import { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth, apiClient } from '../../auth-lib';
import OrgAdminLayout from '../components/OrgAdminLayout';
import { Button, Card, Input, Modal, Select, EmptyState, Skeleton, useToast } from '../../design-system';
import {
  formatServiceName,
  resolveEffectiveDueRule,
  shortDueRule,
  describeDueRule,
  overrideDiffers,
  frequencyLabel,
  FREQUENCY_OPTIONS,
  resolveEffectivePrice,
  shortPrice,
  describePrice,
  priceDiffers,
  suggestBillingBasis,
  BILLING_BASIS_OPTIONS,
  FEE_KIND_OPTIONS,
} from '../../lib/serviceCatalog';
import '../styles/OrgAdminServices.css';

const ADMIN_LEVEL = 5;

const emptyForm = { groupName: '', name: '' };

function OrgAdminServices() {
  const { user } = useAuth();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [pendingChanges, setPendingChanges] = useState({});
  const [savingChanges, setSavingChanges] = useState(false);
  // The last-saved state from the server. Pending changes are diffed against
  // THIS, not just recorded per click — otherwise unticking a service and
  // re-ticking it would still count as a change and be sent to the server.
  const savedEnabled = useRef({});
  // Per-service due-date override editor
  const [dueEditing, setDueEditing] = useState(null);
  const [dueForm, setDueForm] = useState({ frequency: '', dueDay: '', monthOffset: '' });
  const [dueError, setDueError] = useState('');
  const [savingDue, setSavingDue] = useState(false);
  // Per-service base price editor
  const [priceEditing, setPriceEditing] = useState(null);
  const [priceForm, setPriceForm] = useState({ amount: '', billingBasis: '', kind: '', notes: '' });
  const [priceError, setPriceError] = useState('');
  const [savingPrice, setSavingPrice] = useState(false);
  const toast = useToast();

  const canManage = (user?.role?.level || 0) >= ADMIN_LEVEL;

  useEffect(() => {
    fetchServices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchServices = async () => {
    try {
      setLoading(true);
      const res = await apiClient.get('/api/services');
      setServices(res.data);
      savedEnabled.current = Object.fromEntries(res.data.map((s) => [s._id, s.isEnabled]));
      setPendingChanges({});
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  const groups = useMemo(() => {
    const term = search.trim().toLowerCase();
    const filtered = term
      ? services.filter(
          (s) => s.name.toLowerCase().includes(term) || s.groupName.toLowerCase().includes(term)
        )
      : services;

    // Group case-insensitively — the source catalog has both "Accounting"
    // and "accounting" as separate rows for what's the same category.
    const byGroup = new Map();
    filtered.forEach((s) => {
      const key = s.groupName.trim().toLowerCase();
      if (!byGroup.has(key)) byGroup.set(key, { displayName: s.groupName, items: [] });
      byGroup.get(key).items.push(s);
    });
    // Most items first — the group you'd have to scroll through most.
    return Array.from(byGroup.values()).sort((a, b) => b.items.length - a.items.length);
  }, [services, search]);

  const totals = useMemo(() => ({
    all: services.length,
    enabled: services.filter((s) => s.isEnabled).length,
    groups: new Set(services.map((s) => s.groupName.trim().toLowerCase())).size,
    // Counted over services IN USE only. An unpriced service the firm doesn't
    // offer isn't a gap to chase, and counting it would leave this number stuck
    // permanently short of the total.
    priced: services.filter((s) => s.isEnabled && resolveEffectivePrice(s.defaultPrice, s.priceOverride)).length,
  }), [services]);

  // Toggling only updates local state — nothing hits the API until "Save
  // Changes" is clicked, so flipping a bunch of switches doesn't fire a
  // request per click.
  // Records a change only when the value actually differs from what's saved.
  // Toggling something back to its original value REMOVES it from the pending
  // set rather than queueing a no-op write.
  const stagePending = (prev, serviceId, isEnabled) => {
    const next = { ...prev };
    if (savedEnabled.current[serviceId] === isEnabled) {
      delete next[serviceId];
    } else {
      next[serviceId] = isEnabled;
    }
    return next;
  };

  const toggleService = (service, isEnabled) => {
    setServices((prev) => prev.map((s) => (s._id === service._id ? { ...s, isEnabled } : s)));
    setPendingChanges((prev) => stagePending(prev, service._id, isEnabled));
  };

  // Whole-category select/clear. The ROC group alone has 30+ services, so
  // ticking them one at a time is the difference between one click and thirty.
  const toggleGroup = (items, isEnabled) => {
    const ids = new Set(items.map((s) => s._id));
    setServices((prev) => prev.map((s) => (ids.has(s._id) ? { ...s, isEnabled } : s)));
    setPendingChanges((prev) =>
      items.reduce((acc, s) => stagePending(acc, s._id, isEnabled), { ...prev })
    );
  };

  const handleSaveChanges = async () => {
    const entries = Object.entries(pendingChanges);
    if (entries.length === 0) return;

    setSavingChanges(true);
    try {
      // One request, one bulkWrite on the server — not N calls for N toggles.
      const changes = entries.map(([serviceId, isEnabled]) => ({ serviceId, isEnabled }));
      await apiClient.put('/api/services/bulk-selection', { changes });
      // What we just saved becomes the new baseline to diff against.
      entries.forEach(([serviceId, isEnabled]) => {
        savedEnabled.current[serviceId] = isEnabled;
      });
      setPendingChanges({});
      toast.success(`Saved ${entries.length} change${entries.length === 1 ? '' : 's'}`);
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to save changes');
      fetchServices();
    } finally {
      setSavingChanges(false);
    }
  };

  const openDueEditor = (service) => {
    setDueEditing(service);
    setDueError('');
    const eff = resolveEffectiveDueRule(service.defaultDueRule, service.dueRuleOverride);
    setDueForm({
      // Only ever sent for a service with no central cadence — see the note on
      // handleSaveDue. Prefilled from whatever the firm set last time.
      frequency: eff?.frequency ?? '',
      dueDay: eff?.dueDay ?? '',
      // A blank offset means "the following month", the commonest case by far,
      // rather than a form that refuses to submit until a number is typed.
      monthOffset: eff?.monthOffset ?? '',
    });
  };

  const applyOverride = (serviceId, dueRuleOverride) => {
    setServices((prev) => prev.map((s) => (s._id === serviceId ? { ...s, dueRuleOverride } : s)));
  };

  const handleSaveDue = async () => {
    // ⚠️ The frequency is only ours to set where the catalog has no rule to
    // inherit one from. Where it does, the cadence is statutory and the server
    // refuses it — so it is not even sent, and the field is not rendered.
    const ownsCadence = !dueEditing?.defaultDueRule;

    const dueDay = Number(dueForm.dueDay);
    const monthOffset = dueForm.monthOffset === '' ? 1 : Number(dueForm.monthOffset);
    if (ownsCadence && !dueForm.frequency) {
      setDueError('Choose how often this service is filed');
      return;
    }
    if (!Number.isInteger(dueDay) || dueDay < 1 || dueDay > 31) {
      setDueError('Due day must be between 1 and 31');
      return;
    }
    if (!Number.isInteger(monthOffset) || monthOffset < 0) {
      setDueError('Month offset must be 0 or more');
      return;
    }
    setSavingDue(true);
    try {
      const res = await apiClient.put(`/api/services/${dueEditing._id}/selection`, {
        dueRuleOverride: {
          dueDay,
          monthOffset,
          ...(ownsCadence ? { frequency: dueForm.frequency } : {}),
        },
      });
      applyOverride(dueEditing._id, res.data.dueRuleOverride);
      toast.success(ownsCadence ? 'Due date set for your firm' : 'Due date updated for your firm');
      setDueEditing(null);
    } catch (err) {
      setDueError(err.response?.data?.error || 'Failed to save due date');
    } finally {
      setSavingDue(false);
    }
  };

  const handleResetDue = async () => {
    setSavingDue(true);
    try {
      await apiClient.put(`/api/services/${dueEditing._id}/selection`, { dueRuleOverride: null });
      applyOverride(dueEditing._id, null);
      // Clearing a rule the firm wrote from scratch leaves the service with no
      // due date at all; clearing an override falls back to the catalog's. Two
      // different outcomes, so two different sentences.
      toast.success(dueEditing.defaultDueRule ? 'Reset to the default due date' : 'Due date removed');
      setDueEditing(null);
    } catch (err) {
      setDueError(err.response?.data?.error || 'Failed to reset due date');
    } finally {
      setSavingDue(false);
    }
  };

  const openPriceEditor = (service) => {
    setPriceEditing(service);
    setPriceError('');
    const eff = resolveEffectivePrice(service.defaultPrice, service.priceOverride);
    const effRule = resolveEffectiveDueRule(service.defaultDueRule, service.dueRuleOverride);
    setPriceForm({
      amount: eff?.amount ?? '',
      // On a service priced by nobody yet, guess the basis from how often it is
      // filed rather than making someone answer a question the calendar already
      // answers. Only a preselection — it stays editable.
      billingBasis: eff?.billingBasis || suggestBillingBasis(effRule),
      kind: eff?.kind || 'professional_fees',
      notes: eff?.notes || '',
    });
  };

  const applyPrice = (serviceId, priceOverride) => {
    setServices((prev) => prev.map((s) => (s._id === serviceId ? { ...s, priceOverride } : s)));
  };

  const handleSavePrice = async () => {
    const amount = Number(priceForm.amount);
    if (priceForm.amount === '' || !Number.isFinite(amount) || amount < 0) {
      setPriceError('Enter an amount of 0 or more');
      return;
    }
    setSavingPrice(true);
    try {
      const res = await apiClient.put(`/api/services/${priceEditing._id}/selection`, {
        priceOverride: {
          amount,
          billingBasis: priceForm.billingBasis,
          kind: priceForm.kind,
          notes: priceForm.notes,
        },
      });
      applyPrice(priceEditing._id, res.data.priceOverride);
      toast.success('Price saved for your firm');
      setPriceEditing(null);
    } catch (err) {
      setPriceError(err.response?.data?.error || 'Failed to save price');
    } finally {
      setSavingPrice(false);
    }
  };

  const handleClearPrice = async () => {
    setSavingPrice(true);
    try {
      await apiClient.put(`/api/services/${priceEditing._id}/selection`, { priceOverride: null });
      applyPrice(priceEditing._id, null);
      // Clearing falls back to the catalog's indicative rate where there is one,
      // and to no price at all where there isn't. Two outcomes, two sentences.
      toast.success(
        priceEditing.defaultPrice?.amount != null
          ? 'Reset to the suggested price'
          : 'Price removed'
      );
      setPriceEditing(null);
    } catch (err) {
      setPriceError(err.response?.data?.error || 'Failed to clear price');
    } finally {
      setSavingPrice(false);
    }
  };

  // `groupName` prefills the category. Adding a service is nearly always done
  // while looking at the category it belongs to, and retyping the group name
  // into a free-text box is both an extra step and the one chance to misspell
  // it into a brand-new category of one.
  const openAddModal = (groupName = '') => {
    setForm({ ...emptyForm, groupName });
    setFormError('');
    setModalOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setFormError('');

    if (!form.groupName.trim() || !form.name.trim()) {
      setFormError('Group and service name are required');
      return;
    }

    setSaving(true);
    try {
      const existingGroup = services.find(
        (s) => s.groupName.toLowerCase() === form.groupName.trim().toLowerCase()
      );
      const nextItemId = services.reduce((max, s) => Math.max(max, s.itemId), 0) + 1;

      await apiClient.post('/api/services', {
        groupId: existingGroup ? existingGroup.groupId : Math.max(...services.map((s) => s.groupId), 0) + 1,
        groupName: form.groupName.trim(),
        itemId: nextItemId,
        name: form.name.trim(),
      });
      toast.success('Service added');
      setModalOpen(false);
      fetchServices();
    } catch (err) {
      setFormError(err.response?.data?.error || 'Failed to add service');
    } finally {
      setSaving(false);
    }
  };

  return (
    <OrgAdminLayout title="Services" subtitle="Your firm's catalog of services and work categories">
      <div className="services-page">
        {!loading && (
          <div className="services-summary">
            <div className="services-summary-stat">
              <span className="services-summary-number">{totals.enabled}</span>
              <span className="services-summary-label">In Use</span>
            </div>
            <div className="services-summary-stat">
              <span className="services-summary-number">{totals.all}</span>
              <span className="services-summary-label">Total Services</span>
            </div>
            <div className="services-summary-stat">
              <span className="services-summary-number">{totals.groups}</span>
              <span className="services-summary-label">Categories</span>
            </div>
            <div
              className="services-summary-stat"
              title="Services in use that have a base price — what a proposal quotes from"
            >
              <span className="services-summary-number">{totals.priced}<span className="services-summary-of">/{totals.enabled}</span></span>
              <span className="services-summary-label">Priced</span>
            </div>
          </div>
        )}

        {!canManage && (
          <div className="services-readonly-notice">
            <span className="services-readonly-icon">🔒</span>
            Only an Admin can choose which services your firm uses — you can view the catalog here.
          </div>
        )}

        <div className="services-toolbar">
          <Input
            className="services-search"
            placeholder="🔎 Search services…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {canManage && (
            <div className="services-toolbar-actions">
              <Button
                variant="primary"
                onClick={handleSaveChanges}
                loading={savingChanges}
                disabled={Object.keys(pendingChanges).length === 0}
              >
                {savingChanges
                  ? 'Saving…'
                  : `Save Changes${Object.keys(pendingChanges).length ? ` (${Object.keys(pendingChanges).length})` : ''}`}
              </Button>
              <Button variant="secondary" onClick={() => openAddModal()}>+ Add Service</Button>
            </div>
          )}
        </div>

        {loading ? (
          <Card><Skeleton.Text lines={6} /></Card>
        ) : groups.length === 0 ? (
          <EmptyState icon="🗂️" title="No services found" description="Try a different search term." />
        ) : (
          <div className="services-groups">
            {groups.map(({ displayName: groupName, items }) => {
              const enabledCount = items.filter((s) => s.isEnabled).length;

              return (
                <Card key={groupName} className="service-group-card">
                  <div className="service-group-header">
                    <div className="service-group-heading">
                      <div className="service-group-name">{groupName}</div>
                      <div className="service-group-subtitle">{enabledCount} of {items.length} in use</div>
                    </div>
                    {canManage && (
                      <div className="service-group-actions">
                        <label className="service-group-all" title="Select all in this category">
                          <input
                            type="checkbox"
                            checked={enabledCount === items.length}
                            // Partially-selected categories show a dash, not an
                            // empty box — otherwise "all off" and "some on" look
                            // identical in the header.
                            ref={(el) => {
                              if (el) el.indeterminate = enabledCount > 0 && enabledCount < items.length;
                            }}
                            onChange={(e) => toggleGroup(items, e.target.checked)}
                          />
                          <span>All</span>
                        </label>
                        <button
                          type="button"
                          className="service-group-add"
                          title={`Add a service to ${groupName}`}
                          onClick={() => openAddModal(groupName)}
                        >
                          + Add
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="service-check-list">
                    {items.map((s) => {
                      const effRule = resolveEffectiveDueRule(s.defaultDueRule, s.dueRuleOverride);
                      const isOverridden = overrideDiffers(s.defaultDueRule, s.dueRuleOverride);
                      const effPrice = resolveEffectivePrice(s.defaultPrice, s.priceOverride);
                      const isOwnPrice = priceDiffers(s.defaultPrice, s.priceOverride);
                      return (
                        <div
                          key={s._id}
                          className={`service-check-row ${s.isEnabled ? '' : 'service-check-row-off'}`}
                        >
                          <label className="service-check-main">
                            <input
                              type="checkbox"
                              className="service-check-box"
                              checked={s.isEnabled}
                              disabled={!canManage}
                              onChange={(e) => toggleService(s, e.target.checked)}
                            />
                            <span className="service-check-name">{formatServiceName(s.name)}</span>
                          </label>
                          <div className="service-due">
                            {effRule ? (
                              <span
                                className={`service-due-chip ${isOverridden ? 'service-due-chip-custom' : ''}`}
                                title={describeDueRule(effRule)}
                              >
                                {shortDueRule(effRule)}{isOverridden ? ' · custom' : ''}
                              </span>
                            ) : (
                              <span className="service-due-none" title="No default due date set by admin">—</span>
                            )}
                            {/* ⚠️ Every service, not only those the catalog
                                already dates. This was gated on
                                `s.defaultDueRule`, which left a firm unable to
                                put a due date on any service it had invented —
                                precisely the ones no super admin will ever
                                configure. What the dialog lets you change still
                                differs: a central cadence stays fixed. */}
                            {canManage && (
                              <button
                                type="button"
                                className="service-due-edit"
                                onClick={() => openDueEditor(s)}
                              >
                                {effRule ? 'Edit' : 'Set'}
                              </button>
                            )}
                          </div>
                          {/* The firm's base rate — what a proposal for this
                              service starts at. Shown on every service, priced
                              or not, so the unpriced ones are visible as gaps
                              rather than silently absent. */}
                          <div className="service-price">
                            {effPrice ? (
                              <span
                                className={`service-price-chip ${isOwnPrice ? 'service-price-chip-custom' : ''}`}
                                title={
                                  describePrice(effPrice) +
                                  (effPrice.source === 'catalog' ? ' · suggested by the platform catalog' : '')
                                }
                              >
                                {shortPrice(effPrice)}
                              </span>
                            ) : (
                              <span className="service-price-none" title="No base price set">—</span>
                            )}
                            {canManage && (
                              <button
                                type="button"
                                className="service-due-edit"
                                onClick={() => openPriceEditor(s)}
                              >
                                {effPrice ? 'Edit' : 'Price'}
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      <Modal
        isOpen={!!dueEditing}
        onClose={() => setDueEditing(null)}
        title={dueEditing ? `Due date · ${formatServiceName(dueEditing.name)}` : 'Due date'}
        footer={
          <div className="service-due-footer">
            {dueEditing?.dueRuleOverride && (
              <Button variant="secondary" onClick={handleResetDue} disabled={savingDue}>
                {/* Clearing an override falls back to the catalog's rule;
                    clearing a rule the firm wrote leaves no due date at all.
                    Two outcomes, so two labels. */}
                {dueEditing.defaultDueRule ? 'Reset to default' : 'Remove due date'}
              </Button>
            )}
            <div className="service-due-footer-right">
              <Button variant="secondary" onClick={() => setDueEditing(null)} disabled={savingDue}>Cancel</Button>
              <Button variant="primary" onClick={handleSaveDue} loading={savingDue}>Save</Button>
            </div>
          </div>
        }
      >
        {dueEditing && (
          <div className="service-due-form">
            {dueError && <div className="form-error" role="alert">{dueError}</div>}
            {/* Two different jobs behind one button. With a central rule you are
                SHIFTING it, and the cadence is not yours to move; without one
                you are writing the whole rule, because nobody else ever will. */}
            {dueEditing.defaultDueRule ? (
              <>
                <div className="service-due-default-note">
                  <strong>Firm default:</strong> {describeDueRule(dueEditing.defaultDueRule)}
                </div>
                <p className="service-due-help">
                  Frequency ({frequencyLabel(dueEditing.defaultDueRule.frequency)}) is the statutory
                  cadence, set by the platform admin. You can shift the day and month your firm targets.
                </p>
              </>
            ) : (
              <p className="service-due-help">
                The platform catalog sets no due date for this service, so your firm sets the whole
                rule — including how often it is filed. It drives the dates the compliance calendar
                generates.
              </p>
            )}
            <div className="service-due-grid">
              {!dueEditing.defaultDueRule && (
                <Select
                  label="How often is it filed?"
                  // Full width above the day/offset pair: it decides what the
                  // other two mean, so it reads as the first question, not as
                  // one of three equal boxes.
                  className="service-due-span"
                  value={dueForm.frequency}
                  onChange={(e) => setDueForm({ ...dueForm, frequency: e.target.value })}
                >
                  <option value="">Choose…</option>
                  {FREQUENCY_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </Select>
              )}
              <Input
                label="Due day (of month)"
                type="number"
                min={1}
                max={31}
                value={dueForm.dueDay}
                onChange={(e) => setDueForm({ ...dueForm, dueDay: e.target.value })}
                helperText="31 always means the month's last day"
              />
              <Input
                label="Months after period end"
                type="number"
                min={0}
                value={dueForm.monthOffset}
                onChange={(e) => setDueForm({ ...dueForm, monthOffset: e.target.value })}
                helperText="0 = same month, 1 = following month"
              />
            </div>
            {/* Only once the rule is complete — a confident sentence under a
                form that is going to be refused is worse than no sentence. */}
            {(dueEditing.defaultDueRule || dueForm.frequency) && dueForm.dueDay ? (
              <div className="service-due-preview">
                {describeDueRule({
                  frequency: dueEditing.defaultDueRule?.frequency || dueForm.frequency,
                  dueDay: Number(dueForm.dueDay),
                  monthOffset: dueForm.monthOffset === '' ? 1 : Number(dueForm.monthOffset),
                })}
              </div>
            ) : null}
          </div>
        )}
      </Modal>

      <Modal
        isOpen={!!priceEditing}
        onClose={() => setPriceEditing(null)}
        title={priceEditing ? `Base price · ${formatServiceName(priceEditing.name)}` : 'Base price'}
        footer={
          <div className="service-due-footer">
            {priceEditing?.priceOverride && (
              <Button variant="secondary" onClick={handleClearPrice} disabled={savingPrice}>
                {priceEditing.defaultPrice?.amount != null ? 'Reset to suggested' : 'Remove price'}
              </Button>
            )}
            <div className="service-due-footer-right">
              <Button variant="secondary" onClick={() => setPriceEditing(null)} disabled={savingPrice}>Cancel</Button>
              <Button variant="primary" onClick={handleSavePrice} loading={savingPrice}>Save</Button>
            </div>
          </div>
        }
      >
        {priceEditing && (
          <div className="service-due-form">
            {priceError && <div className="form-error" role="alert">{priceError}</div>}
            <p className="service-due-help">
              What your firm normally asks for this service. It is the starting figure on a
              proposal — whoever quotes can still change it for a particular client, and an
              agreement keeps the figure that was actually agreed.
            </p>
            {/* Only worth saying where the platform has actually suggested
                something. On the seeded catalog it never has. */}
            {priceEditing.defaultPrice?.amount != null && (
              <div className="service-due-default-note">
                <strong>Suggested:</strong> {describePrice(priceEditing.defaultPrice)}
              </div>
            )}
            <div className="service-due-grid">
              <Input
                label="Amount"
                type="number"
                min={0}
                step="0.01"
                value={priceForm.amount}
                onChange={(e) => setPriceForm({ ...priceForm, amount: e.target.value })}
                helperText="In your firm's billing currency"
              />
              <Select
                label="Charged"
                value={priceForm.billingBasis}
                onChange={(e) => setPriceForm({ ...priceForm, billingBasis: e.target.value })}
              >
                {BILLING_BASIS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </Select>
              <Select
                label="Fee type"
                // Decides the tax treatment of every line quoted from this
                // price, so it spans rather than sitting as a third small box.
                className="service-due-span"
                value={priceForm.kind}
                onChange={(e) => setPriceForm({ ...priceForm, kind: e.target.value })}
              >
                {FEE_KIND_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </Select>
              <Input
                label="Internal note"
                className="service-due-span"
                placeholder="e.g. up to 50 invoices per return"
                value={priceForm.notes}
                onChange={(e) => setPriceForm({ ...priceForm, notes: e.target.value })}
                helperText="For your team only — never shown to the client"
              />
            </div>
            {priceForm.amount !== '' && Number.isFinite(Number(priceForm.amount)) && (
              <div className="service-due-preview">
                {describePrice({
                  amount: Number(priceForm.amount),
                  billingBasis: priceForm.billingBasis,
                  kind: priceForm.kind,
                })}
              </div>
            )}
          </div>
        )}
      </Modal>

      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title="Add Service"
        footer={
          <>
            <Button variant="secondary" onClick={() => setModalOpen(false)} disabled={saving}>Cancel</Button>
            <Button variant="primary" onClick={handleSave} loading={saving}>
              {saving ? 'Saving…' : 'Add Service'}
            </Button>
          </>
        }
      >
        <form className="service-form" onSubmit={handleSave}>
          {formError && <div className="form-error" role="alert">{formError}</div>}

          <Input
            label="Group *"
            name="groupName"
            list="service-group-options"
            placeholder="e.g. GST, Income Tax, Payroll"
            value={form.groupName}
            onChange={(e) => setForm({ ...form, groupName: e.target.value })}
            disabled={saving}
          />
          <datalist id="service-group-options">
            {Array.from(new Set(services.map((s) => s.groupName))).map((g) => (
              <option key={g} value={g} />
            ))}
          </datalist>

          <Input
            label="Service Name *"
            name="name"
            placeholder="e.g. GST REFUND ON EXPORT OF GOODS"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            disabled={saving}
          />
        </form>
      </Modal>
    </OrgAdminLayout>
  );
}

export default OrgAdminServices;
